import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
  PLATFORM_ID,
  Inject,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  SurveyService,
  SurveyData,
  SurveyQuestion,
  SurveyAnswerItem,
  SurveyResponse,
  SurveySlider,
} from '../survey.service';
import { LanguageService } from '../../services/language/language.service';
import { SurveyI18nService } from '../survey-i18n.service';
import { UserIdService } from '../../services/user-id/user-id.service';

@Component({
    selector: 'app-survey-view',
    imports: [CommonModule, FormsModule],
    templateUrl: './survey-view.component.html',
    styleUrls: ['./survey-view.component.scss']
})
export class SurveyViewComponent
  implements OnInit, OnDestroy, AfterViewInit, OnChanges
{
  @Input() surveyId?: number;
  @Output() surveyClosed = new EventEmitter<void>();

  survey: SurveyData | null = null;
  currentQuestionIndex = 0;
  answers: Map<number, SurveyAnswerItem> = new Map();
  showSurvey = false;
  showThankYou = false;
  isSubmitting = false;
  textDirection: 'rtl' | 'ltr' = 'rtl';

  openAnswer = '';
  selectedSingleChoice: number | null = null;
  selectedMultipleChoices: Set<number> = new Set();
  sliderValue: number | null = null;
  isTextareaFocused = false;

  private localeSubscription?: Subscription;

  constructor(
    private readonly surveyService: SurveyService,
    private readonly languageService: LanguageService,
    private readonly surveyI18n: SurveyI18nService,
    private readonly userIdService: UserIdService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.textDirection = this.languageService.getSelectedLanguage()?.direction ?? 'rtl';

    this.localeSubscription = this.languageService.selectedLanguage$.subscribe(
      (lang) => {
        this.textDirection = lang?.direction ?? 'rtl';
        if (this.surveyId && this.showSurvey) {
          this.resetAndLoadSurvey(this.surveyId);
        }
      }
    );

    if (this.surveyId) {
      this.loadSurvey(this.surveyId);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (changes['surveyId'] && this.surveyId != null) {
      if (!changes['surveyId'].firstChange) {
        this.resetAndLoadSurvey(this.surveyId);
      }
    }
  }

  ngOnDestroy(): void {
    this.localeSubscription?.unsubscribe();
  }

  private resetAndLoadSurvey(surveyId: number): void {
    this.survey = null;
    this.currentQuestionIndex = 0;
    this.answers.clear();
    this.showSurvey = false;
    this.showThankYou = false;
    this.openAnswer = '';
    this.selectedSingleChoice = null;
    this.selectedMultipleChoices = new Set();
    this.sliderValue = null;
    this.loadSurvey(surveyId);
  }

  loadSurvey(surveyId: number): void {
    if (this.userIdService.isPollCompleted(surveyId)) {
      this.surveyClosed.emit();
      return;
    }

    this.surveyService.getSurvey(surveyId).subscribe({
      next: (survey) => {
        if (!survey || !this.surveyService.isSurveyActive(survey)) {
          this.surveyClosed.emit();
          return;
        }
        if (!survey.questions?.length) {
          console.warn(
            'Survey loaded but has no questions; UI is skipped. Add questions in CMS for group id:',
            survey.id
          );
          this.surveyClosed.emit();
          return;
        }
        this.survey = survey;
        this.showSurvey = true;
        this.initializeAnswers();
        this.resetQuestionState();
        this.surveyService.recordSurveyView(survey.id).subscribe();
      },
      error: (error) => {
        console.error('Error loading survey:', error);
        this.surveyClosed.emit();
      },
    });
  }

  private initializeAnswers(): void {}

  getCurrentQuestion(): SurveyQuestion | null {
    if (!this.survey || !this.survey.questions.length) {
      return null;
    }
    return this.survey.questions[this.currentQuestionIndex];
  }

  canProceed(): boolean {
    const question = this.getCurrentQuestion();
    if (!question) return false;
    if (!question.isRequired) return true;

    switch (question.type) {
      case 'Open':
        return this.openAnswer.trim().length > 0;
      case 'SingleChoice':
        return this.selectedSingleChoice !== null;
      case 'MultipleChoice':
        return this.selectedMultipleChoices.size > 0;
      case 'Slider':
        return this.sliderValue !== null && this.sliderValue !== undefined;
      default:
        return false;
    }
  }

  nextQuestion(): void {
    const question = this.getCurrentQuestion();
    if (!question || !this.canProceed()) return;

    this.saveCurrentAnswer();

    if (this.currentQuestionIndex < this.survey!.questions.length - 1) {
      this.currentQuestionIndex++;
      this.resetQuestionState();
    } else {
      this.submitSurvey();
    }
  }

  private saveCurrentAnswer(): void {
    const question = this.getCurrentQuestion();
    if (!question) return;

    switch (question.type) {
      case 'Open':
        if (this.openAnswer.trim()) {
          this.answers.set(question.id, {
            surveyQuestionId: question.id,
            surveyOpenQuestionAnswer: { openText: this.openAnswer },
          });
        }
        break;
      case 'SingleChoice':
        if (this.selectedSingleChoice !== null) {
          this.answers.set(question.id, {
            surveyQuestionId: question.id,
            surveyOptionQuestionAnswer: [
              { questionOptionId: this.selectedSingleChoice },
            ],
          });
        }
        break;
      case 'MultipleChoice':
        if (this.selectedMultipleChoices.size > 0) {
          this.answers.set(question.id, {
            surveyQuestionId: question.id,
            surveyOptionQuestionAnswer: Array.from(
              this.selectedMultipleChoices
            ).map((optionId) => ({ questionOptionId: optionId })),
          });
        }
        break;
      case 'Slider':
        if (this.sliderValue !== null && this.sliderValue !== undefined) {
          this.answers.set(question.id, {
            surveyQuestionId: question.id,
            surveySliderQuestionAnswer: { value: this.sliderValue },
          });
        }
        break;
    }
  }

  private resetQuestionState(): void {
    const question = this.getCurrentQuestion();
    if (!question) return;

    const existingAnswer = this.answers.get(question.id);

    switch (question.type) {
      case 'Open':
        this.openAnswer = existingAnswer?.surveyOpenQuestionAnswer?.openText || '';
        if (isPlatformBrowser(this.platformId)) {
          setTimeout(() => {
            const textarea = document.querySelector(
              '.survey-textarea'
            ) as HTMLTextAreaElement;
            if (textarea) {
              this.adjustTextareaHeight(textarea);
            }
          }, 0);
        }
        break;
      case 'SingleChoice':
        this.selectedSingleChoice =
          existingAnswer?.surveyOptionQuestionAnswer?.[0]?.questionOptionId ||
          null;
        break;
      case 'MultipleChoice': {
        const optionIds =
          existingAnswer?.surveyOptionQuestionAnswer?.map(
            (opt) => opt.questionOptionId
          ) || [];
        this.selectedMultipleChoices = new Set(optionIds);
        break;
      }
      case 'Slider':
        if (question.slider) {
          this.sliderValue =
            existingAnswer?.surveySliderQuestionAnswer?.value ?? null;
        }
        break;
    }
  }

  toggleMultipleChoice(optionId: number): void {
    if (this.selectedMultipleChoices.has(optionId)) {
      this.selectedMultipleChoices.delete(optionId);
    } else {
      this.selectedMultipleChoices.add(optionId);
    }
  }

  submitSurvey(): void {
    if (!this.survey || this.isSubmitting) return;

    this.saveCurrentAnswer();

    const deviceUuid = this.userIdService.getUserId();
    if (!deviceUuid) {
      console.error('Cannot submit survey: User ID not available');
      return;
    }

    const deviceType = this.surveyService.detectDeviceType();

    const response: SurveyResponse = {
      deviceUuid,
      deviceType,
      userLocation: undefined,
      answers: Array.from(this.answers.values()),
    };

    this.isSubmitting = true;

    this.surveyService.submitSurveyResponse(this.survey.id, response).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.userIdService.markPollAsCompleted(this.survey!.id);
        this.showThankYou = true;
      },
      error: (error) => {
        console.error('Error submitting survey:', error);
        this.isSubmitting = false;
        this.userIdService.markPollAsCompleted(this.survey!.id);
        this.showThankYou = true;
      },
    });
  }

  closeSurvey(): void {
    if (this.survey) {
      this.userIdService.markPollAsCompleted(this.survey.id);
    }
    this.showSurvey = false;
    this.showThankYou = false;
    this.surveyClosed.emit();
  }

  translate(key: string): string {
    return this.surveyI18n.translate(key);
  }

  getSurveyContainerStyle(): Record<string, string> {
    if (!this.survey?.backgroundImageUrl) return {};
    return {
      backgroundImage: `url(${this.survey.backgroundImageUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  }

  getQuestionStyle(question: SurveyQuestion): Record<string, string> {
    const styles: Record<string, string> = {};
    if (question.isBold) {
      styles['font-weight'] = 'bold';
    }
    if (question.isItalic) {
      styles['font-style'] = 'italic';
    }
    if (question.isUnderline) {
      styles['text-decoration'] = 'underline';
    }
    return styles;
  }

  getSliderValues(slider: SurveySlider): number[] {
    const values: number[] = [];
    for (let i = slider.maxValue; i >= slider.minValue; i--) {
      values.push(i);
    }
    return values;
  }

  onTextareaInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.adjustTextareaHeight(textarea);
  }

  private adjustTextareaHeight(textarea: HTMLTextAreaElement): void {
    textarea.style.height = 'auto';
    const minHeight = 48;
    textarea.style.height = Math.max(textarea.scrollHeight, minHeight) + 'px';
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId) && this.openAnswer) {
      setTimeout(() => {
        const textarea = document.querySelector(
          '.survey-textarea'
        ) as HTMLTextAreaElement;
        if (textarea) {
          this.adjustTextareaHeight(textarea);
        }
      }, 0);
    }
  }
}
